import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { BowtieDiagram, BowtieNode, BarrierStatus } from './bowtie.types';
import { computeBowtieLayout } from './bowtieLayout';
import { HazardNode } from './nodes/HazardNode';
import { TopEventNode } from './nodes/TopEventNode';
import { ThreatNode } from './nodes/ThreatNode';
import { BarrierNode } from './nodes/BarrierNode';
import { ConsequenceNode } from './nodes/ConsequenceNode';
import { riskService } from '../riskService';
import {
  ShieldAlert,
  Plus,
  X,
  Trash2,
  Save,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Maximize2,
  Minimize2,
  ExternalLink,
} from 'lucide-react';
import { useDialog } from '../../../../shared/DialogContext';

interface BowtieCanvasProps {
  diagram: BowtieDiagram;
  onDiagramUpdated?: (updated: BowtieDiagram) => void;
  height?: string;
}

export const BowtieCanvas: React.FC<BowtieCanvasProps> = ({
  diagram,
  onDiagramUpdated,
  height = '680px',
}) => {
  const { showError } = useDialog();
  // Selected barrier for Inspector Modal
  const [selectedBarrier, setSelectedBarrier] = useState<BowtieNode | null>(null);
  const [inspectorTitle, setInspectorTitle] = useState('');
  const [inspectorStatus, setInspectorStatus] = useState<BarrierStatus>('ok');
  const [inspectorScore, setInspectorScore] = useState<number>(75);
  const [inspectorThreatId, setInspectorThreatId] = useState<string>('');
  const [inspectorConsequenceId, setInspectorConsequenceId] = useState<string>('');
  const [savingBarrier, setSavingBarrier] = useState(false);

  // Add Element Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newElementType, setNewElementType] = useState<
    'preventionBarrier' | 'mitigationBarrier' | 'threat' | 'consequence'
  >('preventionBarrier');
  const [newElementTitle, setNewElementTitle] = useState('');
  const [newElementDesc, setNewElementDesc] = useState('');
  const [newElementDept, setNewElementDept] = useState('');
  const [newElementScore, setNewElementScore] = useState(75);
  const [selectedThreatId, setSelectedThreatId] = useState('');
  const [selectedConsequenceId, setSelectedConsequenceId] = useState('');
  const [addingElement, setAddingElement] = useState(false);

  // Extract threat and consequence nodes from diagram.nodes
  const threatNodes = useMemo(
    () => (diagram.nodes || []).filter((n) => n.type === 'threat'),
    [diagram.nodes]
  );
  const consequenceNodes = useMemo(
    () => (diagram.nodes || []).filter((n) => n.type === 'consequence'),
    [diagram.nodes]
  );

  // Quick action: Add prevention barrier for a specific threat
  const handleOpenAddBarrierForThreat = useCallback((threatNode: BowtieNode) => {
    setNewElementType('preventionBarrier');
    setSelectedThreatId(threatNode.metadata?.causeId || threatNode.id.replace('threat-', ''));
    setShowAddModal(true);
  }, []);

  // Quick action: Add mitigation barrier for a specific consequence
  const handleOpenAddBarrierForConsequence = useCallback((consNode: BowtieNode) => {
    setNewElementType('mitigationBarrier');
    setSelectedConsequenceId(
      consNode.metadata?.consequenceId || consNode.id.replace('consequence-', '')
    );
    setShowAddModal(true);
  }, []);

  // Compute Layout
  const { initialNodes, initialEdges } = useMemo(() => {
    const layout = computeBowtieLayout(diagram);
    // Attach selection listener to barriers and quick action to threats & consequences
    const mappedNodes = layout.nodes.map((node) => {
      if (node.type === 'barrierNode') {
        return {
          ...node,
          data: {
            ...node.data,
            onSelect: (b: BowtieNode) => {
              setSelectedBarrier(b);
              setInspectorTitle(b.label);
              setInspectorStatus(b.metadata?.status || 'ok');
              setInspectorScore(b.metadata?.effectivenessScore ?? 75);
              setInspectorThreatId(b.metadata?.threatId || '');
              setInspectorConsequenceId(b.metadata?.consequenceId || '');
            },
          },
        };
      }
      if (node.type === 'threatNode') {
        return {
          ...node,
          data: {
            ...node.data,
            onAddBarrier: (threat: BowtieNode) => {
              handleOpenAddBarrierForThreat(threat);
            },
          },
        };
      }
      if (node.type === 'consequenceNode') {
        return {
          ...node,
          data: {
            ...node.data,
            onAddBarrier: (cons: BowtieNode) => {
              handleOpenAddBarrierForConsequence(cons);
            },
          },
        };
      }
      return node;
    });
    return { initialNodes: mappedNodes, initialEdges: layout.edges };
  }, [diagram, handleOpenAddBarrierForThreat, handleOpenAddBarrierForConsequence]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Synchronize internal nodes/edges when layout changes or diagram updates
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Fullscreen state & Esc key handler
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showAddModal) {
          setShowAddModal(false);
        } else if (selectedBarrier) {
          setSelectedBarrier(null);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, showAddModal, selectedBarrier]);

  // Node types registry
  const nodeTypes = useMemo(
    () => ({
      hazardNode: HazardNode,
      topEventNode: TopEventNode,
      threatNode: ThreatNode,
      barrierNode: BarrierNode,
      consequenceNode: ConsequenceNode,
    }),
    []
  );

  // Update Barrier Inspector
  const handleSaveBarrier = async () => {
    if (!selectedBarrier || !selectedBarrier.metadata?.controlId) return;
    try {
      setSavingBarrier(true);
      const updated = await riskService.updateBowtieBarrier(
        diagram.riskId,
        selectedBarrier.metadata.controlId,
        {
          title: inspectorTitle,
          barrierStatus: inspectorStatus,
          effectivenessScore: inspectorScore,
          threatId: selectedBarrier.type === 'preventionBarrier' ? (inspectorThreatId || null) : undefined,
          consequenceId: selectedBarrier.type === 'mitigationBarrier' ? (inspectorConsequenceId || null) : undefined,
        }
      );
      setSelectedBarrier(null);
      if (onDiagramUpdated) onDiagramUpdated(updated);
    } catch (err: any) {
      showError(err, 'Failed to Update Barrier');
    } finally {
      setSavingBarrier(false);
    }
  };

  // Delete Barrier
  const handleDeleteBarrier = async () => {
    if (!selectedBarrier || !selectedBarrier.metadata?.controlId) return;
    if (!confirm(`Are you sure you want to remove "${selectedBarrier.label}" from risk controls?`)) return;
    try {
      setSavingBarrier(true);
      const updated = await riskService.deleteBowtieBarrier(
        diagram.riskId,
        selectedBarrier.metadata.controlId
      );
      setSelectedBarrier(null);
      if (onDiagramUpdated) onDiagramUpdated(updated);
    } catch (err: any) {
      showError(err, 'Failed to Delete Barrier');
    } finally {
      setSavingBarrier(false);
    }
  };

  // Add Element to Bowtie & Database
  const handleAddElement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newElementTitle.trim()) return;
    try {
      setAddingElement(true);
      const updated = await riskService.addBowtieElement(diagram.riskId, {
        type: newElementType,
        title: newElementTitle.trim(),
        description: newElementDesc.trim(),
        effectivenessScore: newElementScore,
        ownerDepartment: newElementDept.trim(),
        threatId: newElementType === 'preventionBarrier' && selectedThreatId ? selectedThreatId : undefined,
        consequenceId:
          newElementType === 'mitigationBarrier' && selectedConsequenceId ? selectedConsequenceId : undefined,
      });
      setShowAddModal(false);
      setNewElementTitle('');
      setNewElementDesc('');
      setNewElementDept('');
      setSelectedThreatId('');
      setSelectedConsequenceId('');
      if (onDiagramUpdated) onDiagramUpdated(updated);
    } catch (err: any) {
      showError(err, 'Failed to Add Bowtie Element');
    } finally {
      setAddingElement(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        ...(isFullscreen
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 99999,
              borderRadius: 0,
            }
          : {
              height,
              borderRadius: '16px',
            }),
        background: '#f8fafc',
        border: isFullscreen ? 'none' : '1px solid #e2e8f0',
        overflow: 'hidden',
        position: isFullscreen ? 'fixed' : 'relative',
      }}
    >
      {/* 1. Header Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>🎀</span>
            <strong style={{ fontSize: '15px', color: '#0f172a' }}>{diagram.riskNumber}</strong>
            <span style={{ color: '#64748b', fontSize: '14px' }}>— {diagram.title}</span>
          </div>

          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: '6px',
              background: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
            }}
          >
            Index: {diagram.initialRiskIndex} {diagram.residualRiskIndex ? `→ Residual: ${diagram.residualRiskIndex}` : ''}
          </span>

          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: '6px',
              background: diagram.tolerability === 'INTOLERABLE' ? '#fef2f2' : diagram.tolerability === 'TOLERABLE' ? '#fffbeb' : '#ecfdf5',
              color: diagram.tolerability === 'INTOLERABLE' ? '#b91c1c' : diagram.tolerability === 'TOLERABLE' ? '#b45309' : '#047857',
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            {diagram.tolerability}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Open in New Tab button */}
          <button
            onClick={() => {
              const url = `${window.location.origin}${window.location.pathname}?tab=bowtie&riskId=${encodeURIComponent(diagram.riskId)}`;
              window.open(url, '_blank');
            }}
            title="Open Bowtie diagram in a new browser tab"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 12px',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '8px',
              background: '#f8fafc',
              color: '#334155',
              border: '1px solid #cbd5e1',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <ExternalLink size={14} />
            Open in New Tab
          </button>

          {/* Fullscreen / Minimize button */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Return to normal view (Esc)' : 'Open Bowtie diagram in fullscreen'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 12px',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '8px',
              background: isFullscreen ? '#fee2e2' : '#f8fafc',
              color: isFullscreen ? '#991b1b' : '#334155',
              border: isFullscreen ? '1px solid #fca5a5' : '1px solid #cbd5e1',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {isFullscreen ? 'Minimize (Esc)' : 'Fullscreen'}
          </button>

          <button
            onClick={() => {
              const defaultThreat =
                threatNodes.length > 0
                  ? threatNodes[0].metadata?.causeId || threatNodes[0].id.replace('threat-', '')
                  : '';
              const defaultCons =
                consequenceNodes.length > 0
                  ? consequenceNodes[0].metadata?.consequenceId ||
                    consequenceNodes[0].id.replace('consequence-', '')
                  : '';
              setSelectedThreatId(defaultThreat);
              setSelectedConsequenceId(defaultCons);
              setShowAddModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '8px',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Plus size={14} />
            + Add Element / Barrier
          </button>
        </div>
      </div>

      {/* 2. Bowtie Visual Legend Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 20px',
          background: '#f1f5f9',
          borderBottom: '1px solid #e2e8f0',
          fontSize: '11px',
          color: '#475569',
          fontWeight: 600,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', background: '#ea580c' }} />
          <span>Left Wing: Threats & Causes</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
            <span>Effective (&gt;70%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
            <span>Degraded (40-69%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
            <span>Failed (&lt;40%)</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Right Wing: Consequences & Harm</span>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', background: '#ef4444' }} />
        </div>
      </div>

      {/* 3. React Flow Canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.2}
          maxZoom={1.8}
        >
          <Background color="#cbd5e1" gap={18} size={1} />
          <Controls position="bottom-right" />
          <MiniMap
            position="bottom-left"
            nodeColor={(n) => {
              if (n.type === 'topEventNode') return '#312e81';
              if (n.type === 'hazardNode') return '#f59e0b';
              if (n.type === 'threatNode') return '#ea580c';
              if (n.type === 'consequenceNode') return '#ef4444';
              return '#10b981';
            }}
            style={{ borderRadius: '8px', border: '1px solid #cbd5e1' }}
          />
        </ReactFlow>
      </div>

      {/* 4. Barrier Inspector Drawer / Modal */}
      {selectedBarrier && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '380px',
            height: '100%',
            background: '#ffffff',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
            borderLeft: '1px solid #e2e8f0',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                {selectedBarrier.type === 'preventionBarrier' ? 'Prevention Barrier' : 'Mitigation Barrier'}
              </div>
              <h3 style={{ margin: '4px 0 0 0', fontSize: '16px', color: '#0f172a' }}>Barrier Inspector</h3>
            </div>
            <button
              onClick={() => setSelectedBarrier(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
            >
              <X size={18} />
            </button>
          </div>

          <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Barrier Name
              </label>
              <input
                type="text"
                value={inspectorTitle}
                onChange={(e) => setInspectorTitle(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>

            {selectedBarrier.type === 'preventionBarrier' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Associated Threat / Causal Factor
                </label>
                <select
                  value={inspectorThreatId}
                  onChange={(e) => setInspectorThreatId(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff' }}
                >
                  <option value="">Direct to Top Event / Hazard (General Barrier)</option>
                  {threatNodes.map((t) => {
                    const cId = t.metadata?.causeId || t.id.replace('threat-', '');
                    return (
                      <option key={t.id} value={cId}>
                        🟧 {t.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {selectedBarrier.type === 'mitigationBarrier' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Associated Consequence / Outcome
                </label>
                <select
                  value={inspectorConsequenceId}
                  onChange={(e) => setInspectorConsequenceId(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff' }}
                >
                  <option value="">Direct from Top Event (General Mitigation Barrier)</option>
                  {consequenceNodes.map((c) => {
                    const consId = c.metadata?.consequenceId || c.id.replace('consequence-', '');
                    return (
                      <option key={c.id} value={consId}>
                        🔴 {c.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                Operational Health Status
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setInspectorStatus('ok');
                    setInspectorScore(85);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    border: inspectorStatus === 'ok' ? '2px solid #10b981' : '1px solid #e2e8f0',
                    background: inspectorStatus === 'ok' ? '#ecfdf5' : '#ffffff',
                    color: inspectorStatus === 'ok' ? '#047857' : '#64748b',
                  }}
                >
                  <CheckCircle2 size={16} />
                  Effective
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setInspectorStatus('weak');
                    setInspectorScore(50);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    border: inspectorStatus === 'weak' ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                    background: inspectorStatus === 'weak' ? '#fffbeb' : '#ffffff',
                    color: inspectorStatus === 'weak' ? '#b45309' : '#64748b',
                  }}
                >
                  <AlertTriangle size={16} />
                  Degraded
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setInspectorStatus('failed');
                    setInspectorScore(15);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    border: inspectorStatus === 'failed' ? '2px solid #ef4444' : '1px solid #e2e8f0',
                    background: inspectorStatus === 'failed' ? '#fef2f2' : '#ffffff',
                    color: inspectorStatus === 'failed' ? '#b91c1c' : '#64748b',
                  }}
                >
                  <XCircle size={16} />
                  Failed
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                <span>Effectiveness Score</span>
                <span style={{ color: '#2563eb' }}>{inspectorScore}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={inspectorScore}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setInspectorScore(val);
                  if (val >= 70) setInspectorStatus('ok');
                  else if (val >= 40) setInspectorStatus('weak');
                  else setInspectorStatus('failed');
                }}
                style={{ width: '100%' }}
              />
            </div>

            {selectedBarrier.metadata?.eli5 && (
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#475569', lineHeight: 1.4, marginBottom: '16px' }}>
                <strong style={{ color: '#0f172a' }}>Description:</strong>
                <p style={{ margin: '4px 0 0 0' }}>{selectedBarrier.metadata.eli5}</p>
              </div>
            )}
          </div>

          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              gap: '10px',
              background: '#f8fafc',
            }}
          >
            <button
              type="button"
              onClick={handleDeleteBarrier}
              disabled={savingBarrier}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 14px',
                borderRadius: '8px',
                border: '1px solid #fecaca',
                background: '#ffffff',
                color: '#dc2626',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Trash2 size={14} />
              Delete
            </button>

            <button
              type="button"
              onClick={handleSaveBarrier}
              disabled={savingBarrier}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '9px 14px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Save size={14} />
              {savingBarrier ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* 5. Add Element Modal */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-card modal-md">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Add Bowtie Element</h3>
                <p>Register new barrier, threat, or consequence on the Bowtie model</p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-close-btn"
                title="Close"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddElement}>
              <div className="modal-body">
                <div className="form-field">
                  <label>Element Category</label>
                  <select
                    value={newElementType}
                    onChange={(e) => setNewElementType(e.target.value as any)}
                  >
                    <option value="preventionBarrier">🛡️ Prevention Barrier (Left Wing)</option>
                    <option value="mitigationBarrier">🛡️ Mitigation Barrier (Right Wing)</option>
                    <option value="threat">🟧 Threat / Initiating Cause (Left Wing)</option>
                    <option value="consequence">🔴 Consequence / Outcome (Right Wing)</option>
                  </select>
                </div>

                {newElementType === 'preventionBarrier' && (
                  <div className="form-field">
                    <label>Associated Threat / Causal Factor</label>
                    <select
                      value={selectedThreatId}
                      onChange={(e) => setSelectedThreatId(e.target.value)}
                    >
                      {threatNodes.map((t) => {
                        const cId = t.metadata?.causeId || t.id.replace('threat-', '');
                        return (
                          <option key={t.id} value={cId}>
                            🟧 {t.label}
                          </option>
                        );
                      })}
                      <option value="">Direct to Top Event / Hazard (General Barrier)</option>
                    </select>
                    <small style={{ color: '#64748b', fontSize: '11px', marginTop: '2px', display: 'block' }}>
                      Select which threat this prevention barrier blocks before reaching the Top Event.
                    </small>
                  </div>
                )}

                {newElementType === 'mitigationBarrier' && (
                  <div className="form-field">
                    <label>Associated Consequence / Outcome</label>
                    <select
                      value={selectedConsequenceId}
                      onChange={(e) => setSelectedConsequenceId(e.target.value)}
                    >
                      {consequenceNodes.map((c) => {
                        const consId = c.metadata?.consequenceId || c.id.replace('consequence-', '');
                        return (
                          <option key={c.id} value={consId}>
                            🔴 {c.label}
                          </option>
                        );
                      })}
                      <option value="">Direct from Top Event (General Mitigation Barrier)</option>
                    </select>
                    <small style={{ color: '#64748b', fontSize: '11px', marginTop: '2px', display: 'block' }}>
                      Select which consequence this mitigation barrier protects against after the Top Event.
                    </small>
                  </div>
                )}

                <div className="form-field">
                  <label>Element Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Dual Hydraulic System Redundancy"
                    value={newElementTitle}
                    onChange={(e) => setNewElementTitle(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Description & Scope</label>
                  <textarea
                    rows={3}
                    placeholder="Technical description of control mechanism or operational threat..."
                    value={newElementDesc}
                    onChange={(e) => setNewElementDesc(e.target.value)}
                  />
                </div>

                {(newElementType === 'preventionBarrier' || newElementType === 'mitigationBarrier') && (
                  <>
                    <div className="form-field">
                      <label>Responsible Department / Owner</label>
                      <input
                        type="text"
                        placeholder="e.g. Flight Operations, Maintenance, CAMO"
                        value={newElementDept}
                        onChange={(e) => setNewElementDept(e.target.value)}
                      />
                    </div>

                    <div className="form-field">
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <label>Initial Effectiveness</label>
                        <span style={{ fontWeight: 700, color: '#2563eb' }}>{newElementScore}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={newElementScore}
                        onChange={(e) => setNewElementScore(parseInt(e.target.value, 10))}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={addingElement} className="btn-primary">
                  {addingElement ? 'Adding...' : 'Add to Bowtie Model'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
